import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { 
  Search, Camera, MapPin, ChevronRight, Phone, Clock, 
  Building2, Stethoscope, X, Loader2, Menu, AlertTriangle, Upload,
  Eye, EyeOff, User, FileText, CheckCircle2, MessageCircle, ShoppingBag, Mic, MicOff, Mail, Truck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { formatApiError } from "../lib/formatApiError";
import { toastApiError } from "../lib/toastApiError";
import NotificationBell from "../components/NotificationBell";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { getRoleHomePath, getRoleProfilePath } from "../lib/roleNavigation";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

// Header Component
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { t: tp } = useTranslation("public");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 sm:h-[4.5rem] gap-2 sm:gap-4 min-w-0">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center max-w-[38%] sm:max-w-none" data-testid="logo-link">
            <img src={LOGO_URL} alt="OMU" className="h-9 sm:h-12 md:h-14 w-auto object-contain" />
          </Link>

          {/* Navigation + langue (centre / droite selon écran) */}
          <nav
            className="flex flex-1 items-center justify-end lg:justify-center gap-3 sm:gap-6 lg:gap-8 min-w-0"
            aria-label={tp("navAria")}
          >
            <div className="hidden lg:flex items-center gap-8">
              <Link
                to="/"
                className="text-slate-600 hover:text-omu-blue-500 font-medium transition-colors whitespace-nowrap"
                data-testid="nav-home"
              >
                {t("home")}
              </Link>
              <Link
                to="/search"
                className="text-slate-600 hover:text-omu-blue-500 font-medium transition-colors whitespace-nowrap"
                data-testid="nav-pharmacies"
              >
                {t("pharmacies")}
              </Link>
              <a
                href="#support"
                className="text-slate-600 hover:text-omu-blue-500 font-medium transition-colors whitespace-nowrap"
                data-testid="nav-support"
              >
                {t("support")}
              </a>
            </div>
            <LanguageSwitcher navbar />
          </nav>

          {/* Actions compte */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {/* Auth — desktop */}
          <div className="hidden md:flex items-center gap-2 border-l border-slate-200 pl-2 sm:pl-3 ml-1">
            {user ? (
              <>
                <span className="text-sm text-slate-600">{t("hello", { name: user.name })}</span>
                {user.role === "client" && (
                  <>
                    <NotificationBell />
                    <Button 
                      variant="ghost" 
                      onClick={() => navigate("/messages")}
                      data-testid="messages-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      {tp("messages")}
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={() => navigate("/purchases")}
                      data-testid="purchases-btn"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      {tp("myPurchases")}
                    </Button>
                  </>
                )}
                {user.role === "doctor" && (
                  <>
                    <NotificationBell />
                    <Button variant="ghost" onClick={() => navigate("/doctor/prescriptions")}>
                      {tp("prescriptions")}
                    </Button>
                    <Button variant="ghost" onClick={() => navigate("/messages")}>
                      <MessageCircle className="h-4 w-4 mr-2" /> {tp("chat")}
                    </Button>
                  </>
                )}
                {user.role === "delivery" && (
                  <>
                    <NotificationBell />
                    <Button variant="ghost" onClick={() => navigate("/delivery")}>
                      {tp("deliveries")}
                    </Button>
                  </>
                )}
                {(user.role === "pharmacy" || user.role === "admin" || user.role === "kam" || user.role === "seller") && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate(getRoleHomePath(user.role))}
                    data-testid="dashboard-btn"
                  >
                    {tp("dashboard")}
                  </Button>
                )}
                {getRoleProfilePath(user.role) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate(getRoleProfilePath(user.role))}
                    data-testid="profile-btn"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {tp("myAccount")}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  {t("logout")}
                </Button>
              </>
            ) : (
              <Button 
                className="bg-omu-blue-500 hover:bg-omu-blue-600 text-white rounded-full px-6"
                onClick={() => navigate("/login")}
                data-testid="login-register-btn"
              >
                {t("loginRegisterPro")}
              </Button>
            )}
          </div>

          {/* Mobile : notifications + menu */}
          <div className="flex items-center gap-0.5 md:hidden border-l border-slate-200 pl-2">
          {user && (user.role === "client" || user.role === "doctor" || user.role === "delivery") && <NotificationBell />}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label={tp("menu")}>
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="flex flex-col gap-4 mt-6">
                <Link to="/" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t("home")}
                </Link>
                <Link to="/search" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  {t("pharmacies")}
                </Link>
                <a href="#support" className="text-lg font-medium">{t("support")}</a>
                <hr className="my-4" />
                {user ? (
                  <>
                    <p className="text-sm text-slate-600">{tp("connectedAs", { name: user.name })}</p>
                    {user.role === "client" && (
                      <>
                        <div className="flex items-center gap-2">
                          <NotificationBell />
                          <span className="text-sm">{tp("notifications")}</span>
                        </div>
                        <Button variant="outline" onClick={() => { navigate("/messages"); setMobileMenuOpen(false); }}>
                          <MessageCircle className="h-4 w-4 mr-2" />
                          {tp("messages")}
                        </Button>
                        <Button variant="outline" onClick={() => { navigate("/purchases"); setMobileMenuOpen(false); }}>
                          <ShoppingBag className="h-4 w-4 mr-2" />
                          {tp("myPurchases")}
                        </Button>
                        <Button variant="outline" onClick={() => { navigate("/profile"); setMobileMenuOpen(false); }}>
                          <User className="h-4 w-4 mr-2" />
                          {tp("myAccount")}
                        </Button>
                      </>
                    )}
                    {user.role === "doctor" && (
                      <>
                        <Button variant="outline" onClick={() => { navigate("/doctor/prescriptions"); setMobileMenuOpen(false); }}>{tp("prescriptions")}</Button>
                        <Button variant="outline" onClick={() => { navigate("/messages"); setMobileMenuOpen(false); }}>{tp("chatPatients")}</Button>
                      </>
                    )}
                    {user.role === "delivery" && (
                      <Button variant="outline" onClick={() => { navigate("/delivery"); setMobileMenuOpen(false); }}>{tp("deliveries")}</Button>
                    )}
                    {(user.role === "pharmacy" || user.role === "admin" || user.role === "kam" || user.role === "seller") && (
                      <Button onClick={() => { navigate(getRoleHomePath(user.role)); setMobileMenuOpen(false); }}>
                        {tp("dashboard")}
                      </Button>
                    )}
                    {getRoleProfilePath(user.role) && (
                      <Button variant="outline" onClick={() => { navigate(getRoleProfilePath(user.role)); setMobileMenuOpen(false); }}>
                        <User className="h-4 w-4 mr-2" />
                        {tp("myAccount")}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                      {t("logout")}
                    </Button>
                  </>
                ) : (
                  <Button className="bg-omu-blue-500" onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}>
                    {t("loginRegisterPro")}
                  </Button>
                )}
              </nav>
            </SheetContent>
          </Sheet>
          </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// OCR Modal Component with Multi-medicine support
const OCRModal = ({ isOpen, onClose, onResult }) => {
  const { t } = useTranslation("public");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null);
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const handleFileChange = async (file) => {
    if (!file) return;

    const imageDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
    setPreview(imageDataUrl);

    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiClient.post("/ocr/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.medicines && response.data.medicines.length > 0) {
        onResult({
          medicines: response.data.medicines,
          prescription_meta: response.data.prescription_meta || {},
          detected_text: response.data.detected_text,
          imageDataUrl,
        });
        toast.success(t("medicinesDetected", { count: response.data.medicines.length }));
      } else {
        toast.info(t("noMedicineDetected"));
      }
    } catch (error) {
      toastApiError(error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reset = () => {
    setPreview(null);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("ocrTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!preview ? (
            <div className="space-y-4">
              {/* Option Prendre une photo */}
              <div 
                className="ocr-zone rounded-2xl p-6 text-center cursor-pointer"
                onClick={() => cameraInputRef.current?.click()}
                data-testid="ocr-camera-zone"
              >
                <Camera className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600 font-medium">{t("takePhoto")}</p>
                <p className="text-sm text-slate-400">{t("useCamera")}</p>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  data-testid="ocr-camera-input"
                />
              </div>

              {/* Séparateur */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-base font-medium text-slate-500">{t("or")}</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Option Télécharger une image */}
              <div 
                className="ocr-zone rounded-2xl p-6 text-center cursor-pointer"
                onClick={() => uploadInputRef.current?.click()}
                data-testid="ocr-upload-zone"
              >
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600 font-medium">{t("uploadImage")}</p>
                <p className="text-sm text-slate-400">{t("selectFromDevice")}</p>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  data-testid="ocr-upload-input"
                />
              </div>
            </div>
          ) : (
            <div className="relative">
              <img src={preview} alt="Preview" className="w-full rounded-xl" />
              {isAnalyzing && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
                  <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500 mx-auto" />
                    <p className="mt-2 text-sm text-slate-600">{t("analyzingPrescription")}</p>
                  </div>
                </div>
              )}
              {!isAnalyzing && (
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={reset}
                  data-testid="ocr-reset-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Multi-medicine Results Modal
const MultiMedicineResultsModal = ({ isOpen, onClose, medicines, onSearch }) => {
  const { t } = useTranslation("public");
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-omu-blue-500" />
            {t("detectedMedicinesTitle", { count: medicines.length })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
          {medicines.map((med, index) => (
            <div 
              key={index}
              className="p-4 bg-slate-50 rounded-xl border border-slate-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">
                    {med.detected?.name || t("unknownMedicine")}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {med.detected?.dosage && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {med.detected.dosage}
                      </span>
                    )}
                    {med.detected?.form && (
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                        {med.detected.form}
                      </span>
                    )}
                    {med.detected?.quantity && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        {med.detected.quantity}
                      </span>
                    )}
                  </div>
                  {med.detected?.molecule && (
                    <p className="text-xs text-slate-500 mt-1">
                      {t("molecule")} : {med.detected.molecule}
                    </p>
                  )}
                  {med.detected?.posology && (
                    <p className="text-sm text-omu-blue-700 mt-1.5 bg-omu-blue-50 px-2 py-1 rounded-lg">
                      {t("posology")} : {med.detected.posology}
                    </p>
                  )}
                  {med.matched ? (
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-1.5">
                      <CheckCircle2 className="h-3 w-3" />
                      {t("foundInDatabase")}
                      {med.confidence < 1.0 && med.confidence > 0 && (
                        <span className="text-slate-400 ml-1">({Math.round(med.confidence * 100)}%)</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 flex items-center gap-1 mt-1.5">
                      <AlertTriangle className="h-3 w-3" />
                      {t("notFoundInDatabase")}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="bg-omu-blue-500 hover:bg-omu-blue-600 flex-shrink-0"
                  onClick={() => onSearch(med.detected?.name || med.matched?.name)}
                  data-testid={`search-medicine-${index}`}
                >
                  <Search className="h-3.5 w-3.5 mr-1" />
                  {t("search")}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="pt-4 border-t">
          <Button
            className="w-full bg-omu-blue-500 hover:bg-omu-blue-600"
            onClick={() => {
              const allNames = medicines
                .map(m => m.detected?.name || m.matched?.name)
                .filter(Boolean)
                .join(", ");
              onSearch(allNames);
            }}
            data-testid="search-all-medicines-btn"
          >
            {t("searchAllMedicines")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Login Form Component for visitors
const HomeLoginForm = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const { t: ta } = useTranslation("auth");
  const { t: tp } = useTranslation("public");
  const [loginMethod, setLoginMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const identifier = loginMethod === "email" ? email : phone;
    if (!identifier || !password) {
      toast.error(ta("fillAllFields"));
      return;
    }

    setLoading(true);
    toast.dismiss();
    try {
      const payload = {
        password,
        ...(loginMethod === "email" ? { email } : { phone }),
      };
      const response = await apiClient.post("/auth/login", payload);
      const { access_token, user } = response.data;
      localStorage.setItem("omu_token", access_token);
      setToken(access_token);
      setUser(user);
      toast.success(ta("loginSuccess"));
      navigate(getRoleHomePath(user.role));
    } catch (error) {
      toast.error(formatApiError(error, "AUTH_INVALID_CREDENTIALS"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-soft-lg border-0" data-testid="home-login-card">
      <CardContent className="p-5">
        <h3 className="font-heading text-lg font-semibold text-slate-900">
          {tp("homeLoginTitle")}
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          {tp("homeLoginSubtitle")}
        </p>

        <Tabs value={loginMethod} onValueChange={setLoginMethod} className="mb-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {ta("email")}
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              {ta("phone")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
          {loginMethod === "email" ? (
            <div className="space-y-2">
              <Label htmlFor="home-login-email">{ta("email")}</Label>
              <Input
                id="home-login-email"
                type="email"
                placeholder={ta("emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-xl"
                autoComplete="off"
                data-testid="home-login-email"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="home-login-phone">{ta("phone")}</Label>
              <Input
                id="home-login-phone"
                type="tel"
                placeholder={ta("phonePlaceholder")}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11 rounded-xl"
                autoComplete="off"
                data-testid="home-login-phone"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="home-login-password">{ta("password")}</Label>
            <div className="relative">
              <Input
                id="home-login-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl pr-12"
                autoComplete="current-password"
                data-testid="home-login-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-omu-blue-500 hover:underline">
              {ta("forgotPassword")}
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
            disabled={loading}
            data-testid="home-login-submit"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : tp("homeLoginSubmit")}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-slate-600">
          {ta("noAccount")}{" "}
          <Link
            to="/register/client"
            className="text-omu-blue-500 font-semibold hover:underline"
            data-testid="home-create-account-link"
          >
            {tp("homeLoginCreateAccount")}
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-slate-400">
          {tp("homeLoginProPrompt")}{" "}
          <Link to="/register" className="hover:text-omu-blue-500 underline-offset-2 hover:underline">{tp("homeLoginProSpace")}</Link>
          {" · "}
          <Link to="/register/doctor" className="hover:text-omu-blue-500 underline-offset-2 hover:underline">{tp("homeLoginPrescriberDoctor")}</Link>
        </p>
      </CardContent>
    </Card>
  );
};

// Search Section for logged-in users
const SearchSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("public");
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [detectedMedicines, setDetectedMedicines] = useState([]);
  const [showMedicinesModal, setShowMedicinesModal] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [cnomDialog, setCnomDialog] = useState(null);
  const [cnomInput, setCnomInput] = useState("");
  const recognitionRef = useRef(null);

  const startVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error(t("voiceNotSupported"));
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = i18n.language === "en" ? "en-US" : "fr-FR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === "no-speech") {
        toast.info(t("voiceNoSpeech"));
      } else if (event.error === "not-allowed") {
        toast.error(t("voiceMicDenied"));
      } else {
        toast.error(t("voiceRecognitionError"));
      }
    };
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      toast.success(t("voiceTranscriptSuccess", { transcript }));
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleSearch = (query = searchQuery) => {
    if (!query.trim()) {
      toast.error(t("enterMedicineName"));
      return;
    }

    const params = new URLSearchParams();
    params.set("q", query);
    if (userCoords) {
      params.set("lat", userCoords.lat);
      params.set("lng", userCoords.lng);
    }
    if (location) {
      params.set("city", location);
    }

    navigate(`/search?${params.toString()}`);
  };

  const handleGeolocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocation(t("currentLocation"));
          setIsLocating(false);
          toast.success(t("locationDetected"));
        },
        () => {
          setIsLocating(false);
          toast.error(t("locationFailed"));
        }
      );
    } else {
      setIsLocating(false);
      toast.error(t("geolocationNotSupported"));
    }
  };

  const handleOCRResult = async (payload) => {
    const medicines = Array.isArray(payload) ? payload : (payload?.medicines || []);
    const meta = payload?.prescription_meta || {};
    setShowOCR(false);

    if (user?.role === "client") {
      setCnomDialog({ medicines, meta, payload });
      setCnomInput(meta.cnom || "");
      return;
    }
    if (!user) {
      toast.info(t("loginToValidatePrescription"));
    }
    proceedAfterPrescription(medicines);
  };

  const submitScannedPrescription = async () => {
    if (!cnomDialog) return;
    const { medicines, meta, payload } = cnomDialog;
    const cnom = (cnomInput || "").trim();
    setCnomDialog(null);
    try {
      const res = await apiClient.post("/prescriptions/submit", {
        cnom: cnom || null,
        doctor_name: meta.doctor_name || null,
        image_data: payload?.imageDataUrl || null,
        medicines,
        detected_text: payload?.detected_text || null,
      });
      if (res.data.doctor_found) {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message || t("cnomDoctorNotFound"));
      }
    } catch (error) {
      toastApiError(error);
    }
    proceedAfterPrescription(medicines);
  };

  const proceedAfterPrescription = (medicines) => {
    if (medicines.length > 1) {
      setDetectedMedicines(medicines);
      setShowMedicinesModal(true);
    } else if (medicines.length === 1) {
      const firstMedicine = medicines[0].detected?.name || medicines[0].matched?.name;
      if (firstMedicine) {
        setSearchQuery(firstMedicine);
        handleSearch(firstMedicine);
      }
    }
  };

  const handleMedicineSearch = (query) => {
    setShowMedicinesModal(false);
    handleSearch(query);
  };

  return (
    <>
      <Card className="shadow-soft-lg border-0" data-testid="search-card">
        <CardContent className="p-6 md:p-8">
          {/* OCR Button */}
          <div 
            className="ocr-zone rounded-2xl p-6 text-center cursor-pointer mb-6"
            onClick={() => setShowOCR(true)}
            data-testid="ocr-trigger"
          >
            <div className="flex justify-center gap-4 mb-3">
              <Camera className="h-10 w-10 text-slate-400" />
              <Upload className="h-10 w-10 text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium">{t("ocrTriggerTitle")}</p>
            <p className="text-sm text-slate-400">{t("ocrTriggerHint")}</p>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-300" />
            <span className="text-lg font-semibold text-slate-600">{t("or")}</span>
            <div className="flex-1 h-px bg-slate-300" />
          </div>

          {/* Search Input */}
          <div className="space-y-4">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t("searchPlaceholder")}
                  className="pl-12 h-14 rounded-xl border-slate-200 text-lg search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="search-input"
                />
              </div>
              <Button
                variant="outline"
                className={`h-14 w-14 rounded-xl flex-shrink-0 ${isListening ? "bg-red-50 border-red-300 text-red-500" : ""}`}
                onClick={startVoiceSearch}
                data-testid="voice-search-btn"
              >
                {isListening ? (
                  <MicOff className="h-5 w-5 animate-pulse" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </Button>
            </div>

            {/* Location */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t("locationPlaceholder")}
                  className="pl-12 h-12 rounded-xl border-slate-200"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  data-testid="location-input"
                />
              </div>
              <Button
                variant="outline"
                className="h-12 px-4 rounded-xl"
                onClick={handleGeolocation}
                disabled={isLocating}
                data-testid="geolocation-btn"
              >
                {isLocating ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <MapPin className="h-5 w-5 text-omu-blue-500" />
                )}
              </Button>
            </div>

            {/* Search Button */}
            <Button
              className="w-full h-14 bg-omu-blue-500 hover:bg-omu-blue-600 text-white rounded-full text-lg font-medium btn-press"
              onClick={() => handleSearch()}
              data-testid="search-btn"
            >
              {t("findNow")}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OCR Modal */}
      <OCRModal 
        isOpen={showOCR} 
        onClose={() => setShowOCR(false)} 
        onResult={handleOCRResult}
      />

      {/* Multi-medicine Results Modal */}
      <MultiMedicineResultsModal
        isOpen={showMedicinesModal}
        onClose={() => setShowMedicinesModal(false)}
        medicines={detectedMedicines}
        onSearch={handleMedicineSearch}
      />

      <Dialog open={!!cnomDialog} onOpenChange={(open) => { if (!open) setCnomDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("cnomDialogTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {cnomDialog?.meta?.cnom ? t("cnomDetectedHint") : t("cnomMissingHint")}
          </p>
          <Input
            value={cnomInput}
            onChange={(e) => setCnomInput(e.target.value)}
            placeholder={t("cnomPlaceholder")}
            className="h-12 rounded-xl"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { const meds = cnomDialog?.medicines || []; setCnomDialog(null); proceedAfterPrescription(meds); }}>
              {t("continueWithoutCnom")}
            </Button>
            <Button className="bg-omu-blue-500 hover:bg-omu-blue-600" onClick={submitScannedPrescription}>
              {t("sendToDoctor")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main HomePage Component
const HomePage = () => {
  const { user } = useAuth();
  const { t } = useTranslation("public");
  const { t: tc } = useTranslation("common");
  const [siteSettings, setSiteSettings] = useState({
    contact_email: "ndongeofficiel@gmail.com",
    contact_phone: "+243813776372",
    site_description: ""
  });

  useEffect(() => {
    // Load site settings for footer
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get("/settings/public");
        setSiteSettings(response.data);
      } catch (error) {
        console.log("Using default settings");
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{t("helmetTitle")}</title>
        <meta name="description" content={t("helmetDescription")} />
        <link rel="canonical" href="https://www.omudoc.com/" />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="hero-gradient py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Left — pitch + process (~65–70%) */}
            <div className="md:col-span-8 text-center md:text-left">
              <h1 className="font-heading text-[1.575rem] md:text-[2.1rem] lg:text-[2.625rem] font-bold text-slate-900 tracking-tight leading-tight">
                {t("heroTitle")}
                <span className="text-omu-blue-500">{t("heroTitleHighlight")}</span>
              </h1>
              {!user ? (
                <>
                  <p className="mt-4 text-base md:text-[1.05rem] text-slate-600 max-w-2xl leading-relaxed">
                    <Trans i18nKey="heroGuestIntro" ns="public" components={{ strong: <strong className="font-semibold text-slate-800" /> }} />
                  </p>
                  <div className="mt-7 text-left max-w-2xl mx-auto md:mx-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
                      {t("howItWorks")}
                    </p>
                    <ol className="space-y-2.5">
                      <li className="flex gap-3 rounded-2xl bg-white/90 border border-slate-100 shadow-soft p-3">
                        <span className="h-9 w-9 rounded-full bg-omu-blue-500 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">1</span>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{t("step1Title")}</p>
                          <p className="text-xs text-slate-500 leading-relaxed">{t("step1Desc")}</p>
                        </div>
                      </li>
                      <li className="flex gap-3 rounded-2xl bg-white/90 border border-slate-100 shadow-soft p-3">
                        <span className="h-9 w-9 rounded-full bg-omu-blue-500 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">2</span>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{t("step2Title")}</p>
                          <p className="text-xs text-slate-500 leading-relaxed">{t("step2Desc")}</p>
                        </div>
                      </li>
                      <li className="flex gap-3 rounded-2xl bg-white/90 border border-slate-100 shadow-soft p-3">
                        <span className="h-9 w-9 rounded-full bg-omu-blue-500 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">3</span>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{t("step3Title")}</p>
                          <p className="text-xs text-slate-500 leading-relaxed">{t("step3Desc")}</p>
                        </div>
                      </li>
                    </ol>
                  </div>
                </>
              ) : (
                <p className="mt-3 text-base md:text-lg text-slate-600 max-w-xl">
                  {t("heroLoggedIntro")}
                </p>
              )}
            </div>

            {/* Right — login ~33% */}
            <div className="md:col-span-4">
              {user ? (
                <SearchSection />
              ) : (
                <HomeLoginForm />
              )}
            </div>
          </div>

          {/* Disclaimer - Modifié */}
          <div className="disclaimer-banner mt-8 max-w-2xl mx-auto p-4 rounded-xl flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <Trans i18nKey="disclaimer" ns="public" components={{ strong: <strong /> }} />
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="support" className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={LOGO_URL} alt="OMU" className="h-20 w-auto brightness-0 invert" />
              <p className="mt-4 text-slate-400 text-sm">
                {siteSettings.site_description || t("defaultSiteDescription")}
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">{t("footerNavigation")}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/" className="hover:text-white transition-colors">{tc("home")}</Link></li>
                <li><Link to="/search" className="hover:text-white transition-colors">{t("footerSearch")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">{t("footerProfessionals")}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/register" className="hover:text-white transition-colors">{t("footerProRegister")}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t("footerLogin")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">{t("footerContact")}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {siteSettings.contact_email && (
                  <li>
                    <a href={`mailto:${siteSettings.contact_email}`} className="hover:text-white transition-colors">
                      {siteSettings.contact_email}
                    </a>
                  </li>
                )}
                {siteSettings.contact_phone && (
                  <li>
                    <a href={`tel:${siteSettings.contact_phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                      {siteSettings.contact_phone}
                    </a>
                  </li>
                )}
                {siteSettings.contact_address && (
                  <li className="text-slate-400">{siteSettings.contact_address}</li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            <p>{t("footerCopyright")} | <Link to="/mentions-legales" className="hover:text-white" data-testid="footer-legal-link">{t("footerLegal")}</Link> | <Link to="/politique-confidentialite" className="hover:text-white" data-testid="footer-privacy-link">{t("footerPrivacy")}</Link> | <Link to="/supprimer-mon-compte" className="hover:text-white" data-testid="footer-delete-account-link">{t("footerDeleteAccount")}</Link></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
